<template>

<div class="container-fluid">
    <div class="row">
        <div class="col">
            <!-- Drop zone for drag/drop and paste -->
            <div 
                ref="drop"
                class="kt-upload form-control text-center p-5"
                @dragenter="dragEnter"
                @dragover="dragOver"
                @dragleave="dragLeave"
                @drop="dragDrop"
                @paste="onPaste"
                tabindex="0"
            >
                <span>Drag &amp; Drop your files, <u @click.stop="browse" class="browse-link">Browse</u>, or Paste (Ctrl+V)</span>
            </div>
            <!-- Hidden file input triggered only by Browse click -->
            <input
                :id="props.id"
                ref="upload"
                class="kt-upload-input"
                type="file"
                @change="change"
                :multiple="props.multiple"
            />
        </div>
    </div>
</div>

</template>

<script setup>

import { ref, onMounted } from "vue";

const upload = ref(null);
const drop = ref(null);

const props = defineProps({
    id: 
    {
        type: String,
        default() 
        {
            return "upload-" + (Math.random() + "").substring(2);
        }
    },
    multiple: {
        type: Boolean,
        default: false
    }
});

const emits = defineEmits([
    "process"
]);

const dragEnter = (e) =>
{
    e.preventDefault();
    e.stopPropagation();

    drop.value.style.background = "#e1e1e1";
};

const dragOver = (e) =>
{
    e.preventDefault();
    e.stopPropagation();

    drop.value.style.background = "#e1e1e1";
};

const dragLeave = (e) =>
{
    e.preventDefault();
    e.stopPropagation();

    drop.value.style.background = "transparent";
};

const dragDrop = async (e) =>
{
    e.preventDefault();
    e.stopPropagation();

    drop.value.style.background = "transparent";
    
    let files = [];
    
    // Check for Outlook email drag attempt (browser security prevents direct access)
    const isOutlookEmail = e.dataTransfer.types.includes('multimaillistmessagerows');
    
    // Modern API - supports file drag-and-drop
    if (e.dataTransfer.items) {
        const items = [...e.dataTransfer.items];
        
        for (let i = 0; i <items.length; i++) {
            const item = items[i];
            
            // Try to get file from item
            const file = item.getAsFile();
            
            if (file) {
                files.push(file);
            }
        }
    } else {
        // Fallback for older browsers - use legacy files API
        files = [...e.dataTransfer.files];
    }
    
    // Process files if we have any
    if (files.length > 0) {
        emits("process", files);
    } else if (isOutlookEmail) {
        // Show helpful message for Outlook email limitation
        alert("To attach Outlook emails:\n\n1. Drag the email to your Desktop first (saves as .msg file)\n2. Then drag the .msg file here\n\nOR\n\nRight-click the email → Save As → then upload the saved file\n\n(Browser security prevents direct email drag-and-drop)");
    }
};

const change = (e) =>
{
    if (upload.value.files.length)
    {
        emits("process", upload.value.files);
    }
};

const browse = (e) =>
{
    e.preventDefault();
    e.stopPropagation();
    upload.value?.click();
};

const onPaste = async (e) =>
{
    e.preventDefault();
    e.stopPropagation();
    
    const files = [];
    const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "").slice(0, 13); // YYYYMMDD-HHMMSS
    
    // Modern API - requires HTTPS and user gesture (paste event counts as gesture)
    if (navigator.clipboard && navigator.clipboard.read) {
        try {
            const clipboardItems = await navigator.clipboard.read();
            for (const item of clipboardItems) {
                const imageType = item.types.find(t => t.startsWith("image/"));
                if (imageType) {
                    const blob = await item.getType(imageType);
                    const ext = imageType === "image/png" ? "png" : "jpg";
                    const file = new File([blob], `Pasted-${timestamp}.${ext}`, { type: imageType });
                    files.push(file);
                }
            }
        } catch (err) {
            console.error("Clipboard read failed:", err);
        }
    }
    
    // Fallback: check event.clipboardData for browsers without navigator.clipboard.read()
    if (files.length === 0 && e.clipboardData?.items) {
        for (let i = 0; i < e.clipboardData.items.length; i++) {
            const item = e.clipboardData.items[i];
            if (item.type.startsWith("image/")) {
                const blob = item.getAsFile();
                if (blob) {
                    const ext = item.type === "image/png" ? "png" : "jpg";
                    const file = new File([blob], `Screenshot-${timestamp}.${ext}`, { type: item.type });
                    files.push(file);
                }
            }
        }
    }
    
    if (files.length > 0) {
        emits("process", files);
    }
};

onMounted(() =>
{
    // ... nothing   
});

</script>

<style scoped>

.kt-upload {
    border: 1px dotted #dedede;
    cursor: default;
}

.kt-upload-input {
    position: absolute;
    visibility: hidden;
}

.browse-link {
    cursor: pointer;
    color: #0d6efd;
}

.browse-link:hover {
    text-decoration: underline;
}
    

</style>